import * as React from 'react';
import {
  useFloating,
  autoUpdate,
  offset,
  size,
  flip,
  shift,
  useClick,
  useDismiss,
  useRole,
  useInteractions,
  useMergeRefs,
  Placement,
  FloatingPortal,
  FloatingFocusManager,
  useId,
} from '@floating-ui/react';
import { SIDEBAR_ZINDEX_BASE } from '../sidebar-right/sidebar.component';

interface PopoverOptions {
  floatingOffset?: Parameters<typeof offset>[0];
  initialOpen?: boolean;
  placement?: Placement;
  modal?: boolean;
  open?: boolean;
  shouldFlip?: boolean;
  shouldMatchReferenceWidth?: boolean;
  strategy?: 'fixed' | 'absolute';
  shouldReturnFocus?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function usePopover({
  floatingOffset = 5,
  initialOpen = false,
  placement = 'bottom',
  modal,
  open: controlledOpen,
  onOpenChange: setControlledOpen,
  shouldFlip = true,
  shouldMatchReferenceWidth,
  strategy = 'absolute',
  shouldReturnFocus = true,
}: PopoverOptions = {}) {
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(initialOpen);
  const [labelId, setLabelId] = React.useState<string | undefined>();
  const [descriptionId, setDescriptionId] = React.useState<
    string | undefined
  >();

  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = setControlledOpen ?? setUncontrolledOpen;

  const data = useFloating({
    strategy,
    placement,
    open,
    onOpenChange: setOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(floatingOffset),
      shouldFlip
        ? flip({
            crossAxis: placement.includes('-'),
            fallbackAxisSideDirection: 'end',
            padding: 5,
          })
        : undefined,
      shift({ padding: 5 }),
      shouldMatchReferenceWidth
        ? size({
            apply({ rects, elements }) {
              Object.assign(elements.floating.style, {
                width: `${rects.reference.width}px`,
              });
            },
          })
        : undefined,
    ],
  });

  const { context } = data;

  const click = useClick(context, {
    enabled: controlledOpen == null,
  });
  const dismiss = useDismiss(context);
  const role = useRole(context);

  const interactions = useInteractions([click, dismiss, role]);

  return React.useMemo(
    () => ({
      open,
      setOpen,
      ...interactions,
      ...data,
      modal,
      shouldReturnFocus,
      labelId,
      descriptionId,
      setLabelId,
      setDescriptionId,
    }),
    [
      open,
      setOpen,
      interactions,
      data,
      modal,
      shouldReturnFocus,
      labelId,
      descriptionId,
    ]
  );
}

type ContextType =
  | (ReturnType<typeof usePopover> & {
      setLabelId: React.Dispatch<React.SetStateAction<string | undefined>>;
      setDescriptionId: React.Dispatch<
        React.SetStateAction<string | undefined>
      >;
    })
  | null;

const PopoverContext = React.createContext<ContextType>(null);

export const usePopoverContext = () => {
  const context = React.useContext(PopoverContext);

  if (context == null) {
    throw new Error('Popover components must be wrapped in <Popover />');
  }

  return context;
};

export function Popover({
  children,
  modal = false,
  ...restOptions
}: {
  children: React.ReactNode;
} & PopoverOptions) {
  // This can accept any props as options, e.g. `placement`,
  // or other positioning options.
  const popover = usePopover({ modal, ...restOptions });
  return (
    <PopoverContext.Provider value={popover}>
      {children}
    </PopoverContext.Provider>
  );
}

interface PopoverTriggerProps {
  children: React.ReactNode;
  asChild?: boolean;
}

export const PopoverTrigger = React.forwardRef<
  HTMLElement,
  React.HTMLProps<HTMLElement> & PopoverTriggerProps
>(({ children, asChild = false, ...props }, propRef) => {
  const context = usePopoverContext();
  const childrenRef = (children as any).ref;
  const ref = useMergeRefs([context.refs.setReference, propRef, childrenRef]);

  // `asChild` allows the user to pass any element as the anchor
  if (asChild && React.isValidElement(children)) {
    return React.cloneElement(
      children,
      context.getReferenceProps({
        ref,
        ...props,
        ...children.props,
        'data-state': context.open ? 'open' : 'closed',
      })
    );
  }

  return (
    <button
      ref={ref}
      type='button'
      // The user can style the trigger based on the state
      data-state={context.open ? 'open' : 'closed'}
      {...context.getReferenceProps(props)}>
      {children}
    </button>
  );
});

export const PopoverContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLProps<HTMLDivElement> & { isInPortal?: boolean }
>(({ style, isInPortal = true, ...props }, propRef) => {
  const { context: floatingContext, ...context } = usePopoverContext();
  const ref = useMergeRefs([context.refs.setFloating, propRef]);

  if (!floatingContext.open) return null;
  const Wrapper = isInPortal ? FloatingPortal : React.Fragment;

  return (
    <Wrapper>
      <FloatingFocusManager
        context={floatingContext}
        modal={context.modal}
        returnFocus={context.shouldReturnFocus}>
        <div
          ref={ref}
          style={{
            ...context.floatingStyles,
            ...style,
            zIndex: SIDEBAR_ZINDEX_BASE + 10000, // TODO: think about more inteligent zIndex calcualtions for cases when we need to consider multiple opened sidebars
          }}
          aria-labelledby={context.labelId}
          aria-describedby={context.descriptionId}
          {...context.getFloatingProps(props)}>
          {props.children}
        </div>
      </FloatingFocusManager>
    </Wrapper>
  );
});

export const PopoverHeading = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLProps<HTMLHeadingElement>
>((props, ref) => {
  const { setLabelId } = usePopoverContext();
  const id = useId();

  // Only sets `aria-labelledby` on the Popover root element
  // if this component is mounted inside it.
  React.useLayoutEffect(() => {
    setLabelId(id);
    return () => setLabelId(undefined);
  }, [id, setLabelId]);

  return (
    <h2
      {...props}
      ref={ref}
      id={id}>
      {props.children}
    </h2>
  );
});

export const PopoverDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLProps<HTMLParagraphElement>
>((props, ref) => {
  const { setDescriptionId } = usePopoverContext();
  const id = useId();

  // Only sets `aria-describedby` on the Popover root element
  // if this component is mounted inside it.
  React.useLayoutEffect(() => {
    setDescriptionId(id);
    return () => setDescriptionId(undefined);
  }, [id, setDescriptionId]);

  return (
    <p
      {...props}
      ref={ref}
      id={id}
    />
  );
});

interface PopoverCloseProps {
  children: React.ReactElement;
  // asChild?: boolean;
}

export const PopoverClose = React.forwardRef<
  HTMLButtonElement,
  PopoverCloseProps
>((props, ref) => {
  const { setOpen } = usePopoverContext();

  // `asChild` allows the user to pass any element as the anchor
  // if (props.asChild && React.isValidElement(props.children)) {
  return React.cloneElement(props.children, {
    ref,
    onClick: (event: MouseEvent) => {
      props.children.props.onClick && props.children.props.onClick(event);
      setOpen(false);
    },
  });
  // }
});
